/**
 * cacheApertures.ts — the cache sites on a planet, made visible (P6b).
 *
 * In the HomesteadMarkers / ResourceNodes family: every marker is parented to its
 * planet's `group`, so it rides the planet's orbit and spin for free — no per-frame
 * repositioning, and a marker is always exactly where the site is.
 *
 * The catalog itself is pure `f(planet seed)` — `cacheSites()` in
 * `shared/src/cache/sites.ts` — so nothing here is fetched, synced or persisted. That
 * is also why it is built PER PLANET on demand rather than for the whole system:
 * `cacheSites` runs a `PlanetSampler` and a settlement-influence query per candidate
 * (30–50 of them), which is fine once for the world you are standing on and wasteful
 * for eight worlds you are flying past.
 *
 * SILHOUETTE PER THEME. A player should be able to tell a vault from a hollow across a
 * valley, before any prompt is in range: `ruin` is a broken arch, `vault` a sealed slab
 * in a frame, `hollow` a ring of jagged teeth. Each carries a light beam so the site
 * reads at ~300 m, and a slow pulse so it reads as a live thing rather than scenery.
 *
 * ⚠ The pulse LIGHTS are capped ({@link MAX_LIGHTS}). Sites are common — a dense planet
 * puts 20+ apertures on one body, and a PointLight each would cost more than the whole
 * dungeon does. The nearest few get real lights; the rest pulse emissively.
 */
import * as THREE from "three";
import { cacheSites, type CacheSite, type CacheTheme } from "@hyperlight/shared";
import type { Planet } from "./planet";

/** How many apertures may hold a real PointLight at once (nearest-first). */
export const MAX_LIGHTS = 4;
/** Marker glow beam height, metres — sized to read at ~300 m. */
const BEAM_H = 26;
/**
 * FAR BEACON — the "you can see it from across the map" layer (user call 2026-07-29).
 * A second, much taller beam that is INVISIBLE up close (it would drown the marker and
 * the dungeon mouth you are standing at) and fades IN with viewer distance, its width
 * scaled with distance so it holds roughly constant apparent size instead of thinning
 * to a sub-pixel thread. Its material sets `fog: false` — planet fog is what really
 * killed long-range visibility, not geometry size; an additive shaft that ignores fog
 * reads for kilometres, the same exemption the sky/star layers use.
 */
const FAR_BEAM_H = 420;
/** Far beacon fade-in band, metres: 0 at NEAR, full at FAR. */
const FAR_FADE_NEAR_M = 220;
const FAR_FADE_FAR_M = 700;
/** Width grows with distance/this, clamped — apparent size roughly holds. Tuned against
 *  a 1.5 km screenshot (2026-07-29): 650 read as a wisp; 260 reads as a landmark. */
const FAR_WIDTH_PER_M = 260;
const FAR_WIDTH_MAX = 14;
/** Peak opacity — a landmark, not a searchlight show. 0.65 with NORMAL blending was the
 *  first value that still read on the worst hue overlap measured (amber beam, yellow
 *  desert sky, 2026-07-29). */
const FAR_OPACITY = 0.65;

interface Aperture {
  site: CacheSite;
  /** Planet-local surface point (metres). */
  local: THREE.Vector3;
  group: THREE.Group;
  glow: THREE.MeshBasicMaterial;
  /** The far beacon's own material + mesh — opacity/width are driven per frame. */
  farGlow: THREE.MeshBasicMaterial;
  farBeam: THREE.Mesh;
  light: THREE.PointLight | null;
}

/** What {@link CacheApertures.nearest} answers with. */
export interface NearestAperture {
  site: CacheSite;
  tier: number;
  theme: CacheTheme;
  /** Lateral distance from the query point, metres. */
  dist: number;
  /** Planet-local surface point. */
  local: THREE.Vector3;
}

export class CacheApertures {
  private apertures: Aperture[] = [];
  private planetId: string | null = null;
  private pulse = 0;
  private readonly disposables: { dispose(): void }[] = [];
  private readonly _v = new THREE.Vector3();

  /** Sites currently marked (probe readback). */
  get count(): number {
    return this.apertures.length;
  }

  /** The planet these markers belong to, or null when nothing is marked. */
  get planet(): string | null {
    return this.planetId;
  }

  /**
   * Mark every cache site on `planet`. Idempotent per planet id, so this is safe to
   * call from the foot-planet watch every frame; passing null clears.
   */
  setPlanet(planet: Planet | null, planetIndex: number): void {
    const id = planet?.def.id ?? null;
    if (id === this.planetId) return;
    this.clear();
    this.planetId = id;
    if (planet === null) return;
    const sites = cacheSites(planet.def, planetIndex);
    for (const site of sites) {
      const dir = this._v.set(site.dir[0], site.dir[1], site.dir[2]);
      const r = planet.sampler.surfaceRadius(dir.x, dir.y, dir.z);
      const local = dir.clone().multiplyScalar(r);
      const built = this.build(site, local);
      planet.group.add(built.group);
      this.apertures.push(built);
    }
  }

  /**
   * The nearest aperture to a PLANET-LOCAL point, by LATERAL distance (the frame-free
   * measure every cache proximity gate uses — an aperture on the shelf below you is
   * still the aperture you are standing at). Null when the planet has none.
   */
  nearest(local: THREE.Vector3): NearestAperture | null {
    let best: Aperture | null = null;
    let bestD = Infinity;
    for (const a of this.apertures) {
      // Chord between the two radials, scaled to the surface: altitude-independent.
      const d = lateralDistance(local, a.local);
      if (d < bestD) { bestD = d; best = a; }
    }
    if (best === null) return null;
    return { site: best.site, tier: best.site.tier, theme: best.site.theme, dist: bestD, local: best.local };
  }

  /** Every marked site (the minimap dot list). */
  list(): readonly { site: CacheSite; local: THREE.Vector3 }[] {
    return this.apertures.map((a) => ({ site: a.site, local: a.local }));
  }

  /**
   * Slow pulse + the nearest-N light budget. `local` is the viewer's planet-local
   * position; pass null to leave the lights where they are.
   */
  update(dt: number, local: THREE.Vector3 | null): void {
    if (this.apertures.length === 0) return;
    this.pulse = (this.pulse + dt * 0.9) % (Math.PI * 2);
    const k = 0.55 + 0.45 * (0.5 + 0.5 * Math.sin(this.pulse));
    for (const a of this.apertures) {
      a.glow.opacity = 0.28 + 0.34 * k;
      if (a.light) a.light.intensity = 10 + 14 * k;
    }
    if (local === null) return;
    // Re-deal the light budget nearest-first. Cheap (a partial sort over ~20 items).
    const order = this.apertures
      .map((a, i) => [lateralDistance(local, a.local), i] as const)
      .sort((p, q) => p[0] - q[0]);
    order.forEach(([d, i], rank) => {
      const a = this.apertures[i];
      const want = rank < MAX_LIGHTS;
      if (a.light) a.light.visible = want;

      // FAR BEACON: fade in over [NEAR, FAR], hold width ∝ distance so the shaft keeps
      // apparent size instead of thinning to a thread. The same distance the light
      // budget just computed — no extra pass.
      const t = Math.min(1, Math.max(0, (d - FAR_FADE_NEAR_M) / (FAR_FADE_FAR_M - FAR_FADE_NEAR_M)));
      a.farBeam.visible = t > 0;
      if (t > 0) {
        a.farGlow.opacity = FAR_OPACITY * t * (0.8 + 0.2 * k); // keep a touch of the pulse
        const w = Math.min(FAR_WIDTH_MAX, Math.max(1, d / FAR_WIDTH_PER_M));
        a.farBeam.scale.set(w, 1, w);
      }
    });
  }

  clear(): void {
    for (const a of this.apertures) {
      a.group.parent?.remove(a.group);
      a.group.clear();
    }
    this.apertures = [];
    this.planetId = null;
  }

  dispose(): void {
    this.clear();
    for (const d of this.disposables) d.dispose();
    this.disposables.length = 0;
  }

  private keep<T extends { dispose(): void }>(o: T): T {
    this.disposables.push(o);
    return o;
  }

  /** One themed marker, oriented so +Y is the local outward radial. */
  private build(site: CacheSite, local: THREE.Vector3): Aperture {
    const [structure, glowColor] = PALETTE[site.theme];
    const group = new THREE.Group();
    group.name = `cacheAperture:${site.id}`;
    group.position.copy(local);
    // Seat +Y on the outward radial — the marker stands up out of the ground.
    group.quaternion.setFromUnitVectors(UP, this._v.copy(local).normalize());

    const mat = this.keep(new THREE.MeshStandardMaterial({
      color: structure, roughness: 0.85, metalness: 0.15, flatShading: true,
    }));
    const glow = this.keep(new THREE.MeshBasicMaterial({
      color: glowColor, transparent: true, opacity: 0.45, depthWrite: false,
      side: THREE.DoubleSide,
    }));

    if (site.theme === "vault") {
      // A sealed slab in a frame: two jambs, a lintel, a lit door panel.
      const jamb = this.keep(new THREE.BoxGeometry(0.9, 7.4, 1.5));
      for (const s of [-1, 1]) {
        const m = new THREE.Mesh(jamb, mat);
        m.position.set(s * 2.6, 3.7, 0);
        group.add(m);
      }
      const lintel = this.keep(new THREE.BoxGeometry(6.9, 1.3, 1.9));
      const top = new THREE.Mesh(lintel, mat);
      top.position.set(0, 8.0, 0);
      group.add(top);
      const panel = this.keep(new THREE.PlaneGeometry(4.2, 7.0));
      const door = new THREE.Mesh(panel, glow);
      door.position.set(0, 3.6, 0);
      group.add(door);
    } else if (site.theme === "hollow") {
      // A ring of jagged teeth around a dark mouth.
      const tooth = this.keep(new THREE.ConeGeometry(0.85, 4.6, 5));
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2;
        const m = new THREE.Mesh(tooth, mat);
        m.position.set(Math.cos(a) * 3.6, 2.0, Math.sin(a) * 3.6);
        m.rotation.set(Math.cos(a) * 0.28, 0, -Math.sin(a) * 0.28);
        group.add(m);
      }
      const maw = this.keep(new THREE.CircleGeometry(3.1, 20));
      const disc = new THREE.Mesh(maw, glow);
      disc.rotation.x = -Math.PI / 2;
      disc.position.y = 0.4;
      group.add(disc);
    } else {
      // ruin: a broken arch — two leaning pillars and a fallen lintel stub.
      const pillar = this.keep(new THREE.BoxGeometry(1.2, 8.2, 1.2));
      for (const s of [-1, 1]) {
        const m = new THREE.Mesh(pillar, mat);
        m.position.set(s * 3.0, 4.1, 0);
        m.rotation.z = -s * 0.12;
        group.add(m);
      }
      const stub = this.keep(new THREE.BoxGeometry(3.4, 1.2, 1.6));
      const span = new THREE.Mesh(stub, mat);
      span.position.set(-1.9, 8.5, 0);
      span.rotation.z = 0.22;
      group.add(span);
      const gate = this.keep(new THREE.PlaneGeometry(4.6, 6.4));
      const veil = new THREE.Mesh(gate, glow);
      veil.position.set(0, 3.4, 0);
      group.add(veil);
    }

    // The beam: what actually makes a site findable from across a valley.
    const beamGeo = this.keep(new THREE.CylinderGeometry(0.5, 1.4, BEAM_H, 8, 1, true));
    const beam = new THREE.Mesh(beamGeo, glow);
    beam.position.y = BEAM_H / 2;
    group.add(beam);

    // The FAR beacon (see the constants header): fog-exempt, invisible up close, faded
    // in + width-scaled by update() so it reads for kilometres. NORMAL blending, not
    // additive — additive light over a bright day sky sums to sky (measured 2026-07-29:
    // the shaft vanished above the treeline at 1.5 km); a normally-blended tinted shaft
    // keeps its hue against sky and ground alike. depthWrite stays off; open-ended
    // cylinder so there is no lid to catch light.
    const farGlow = new THREE.MeshBasicMaterial({
      color: glowColor,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide,
      fog: false,
    });
    this.keep(farGlow);
    const farGeo = this.keep(new THREE.CylinderGeometry(0.9, 1.6, FAR_BEAM_H, 8, 1, true));
    const farBeam = new THREE.Mesh(farGeo, farGlow);
    farBeam.position.y = FAR_BEAM_H / 2;
    farBeam.visible = false; // update() owns visibility — stays dark until you are far
    group.add(farBeam);

    const light = new THREE.PointLight(glowColor, 14, 46);
    light.position.y = 2.4;
    light.visible = false; // the budget deal in update() turns the nearest few on
    group.add(light);

    return { site, local, group, glow, farGlow, farBeam, light };
  }
}

const UP = new THREE.Vector3(0, 1, 0);

/** [structure colour, glow colour] per theme. */
const PALETTE: Record<CacheTheme, [number, number]> = {
  ruin: [0x9c8f77, 0xffb444],
  vault: [0x8f9aa8, 0x59d6ff],
  hollow: [0x6b6357, 0x76e39a],
};

/** Minimap dot colour per theme (CSS). Same hues as {@link PALETTE}'s glow. */
export const APERTURE_MAP_COLOR: Record<CacheTheme, string> = {
  ruin: "#ffb444",
  vault: "#59d6ff",
  hollow: "#76e39a",
};

/**
 * Lateral (great-circle chord) distance between two planet-local points, in metres.
 * Radials, not raw positions: a player 30 m up a slope is not 30 m from the aperture.
 */
function lateralDistance(a: THREE.Vector3, b: THREE.Vector3): number {
  const la = a.length() || 1;
  const lb = b.length() || 1;
  const dx = a.x / la - b.x / lb;
  const dy = a.y / la - b.y / lb;
  const dz = a.z / la - b.z / lb;
  return Math.hypot(dx, dy, dz) * lb;
}
